import React, { useRef, useEffect } from "react";
import {
  LocationDetailsProps,
  MainProps,
  AboutShopProps,
  MapSectionProps,
} from "./types";
import { mapContainerStyle, mapStyles } from "./data";
import {
  animateLocationDetails,
  animateMap,
  cleanupScrollTriggers,
} from "./gsap";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from "@react-google-maps/api";
import { BUSINESS_HOURS } from "@/core/constants/business";
import { formatTime } from "@/core/utils/formatTime";

// Format business hours for display
const getFormattedHours = () => {
  const hours = BUSINESS_HOURS.monday;

  if (typeof hours === "string") {
    return hours;
  }

  return `${formatTime(hours.open)} – ${formatTime(hours.close)}`;
};

// Get shortened formatted hours (10:00 AM → 10AM)
const getShortFormattedHours = () => {
  const hours = BUSINESS_HOURS.monday;

  if (typeof hours === "string") {
    return hours;
  }

  return `${hours.open.replace(/^(\d+):00/, "$1")}am - ${hours.close.replace(
    /^(\d+):00/,
    "$1"
  )}pm`;
};

// Location Details Component
const LocationDetails: React.FC<LocationDetailsProps> = ({ locationData }) => {
  const formattedHours = getFormattedHours();

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
      <h3 className="text-2xl font-semibold mb-4">Location Details</h3>
      <div className="space-y-4">
        <div>
          <h4 className="font-semibold text-cannabis-green-600">Address</h4>
          <p>{locationData.address.street}</p>
          <p>
            {locationData.address.city}, {locationData.address.state}{" "}
            {locationData.address.zip}
          </p>
        </div>

        <div>
          <h4 className="font-semibold text-cannabis-green-600">Hours</h4>
          <p className="flex justify-between">
            <span>Every Day:</span>
            <span>{formattedHours}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

// About Shop Component
const AboutShop: React.FC<AboutShopProps> = ({ city }) => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-8">
      <h3 className="text-2xl font-semibold mb-4">About Our Shop</h3>
      <p className="mb-4">
        Our {city} dispensary offers a premium selection of cannabis products in
        a welcoming environment. Located in the vibrant Cleveland Park
        neighborhood, we're easily accessible and ready to serve you.
      </p>
      <p className="mb-4">
        Our knowledgeable staff is dedicated to helping you find the perfect
        products for your needs, whether you're a cannabis connoisseur or new to
        the experience.
      </p>
      <p>
        Visit us today to explore our selection of premium flower, vape
        cartridges, edibles, concentrates, and more!
      </p>
    </div>
  );
};

// Map Section Component
const MapSection: React.FC<MapSectionProps> = ({ locationData }) => {
  const [isInfoWindowOpen, setIsInfoWindowOpen] = React.useState(false);
  const shortFormattedHours = getShortFormattedHours();

  // Load the Google Maps API
  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBFUIzxEwVOnNgLtjKbUi2sLhdw0EbdPjw",
  });

  // Open the info window when the map loads
  const handleMapLoad = () => {
    setIsInfoWindowOpen(true);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-8 h-full">
      <h3 className="text-2xl font-semibold mb-6">Find Us</h3>

      {/* Map Container */}
      <div className="h-[500px] rounded-lg overflow-hidden">
        {isLoaded ? (
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={locationData.coordinates}
            zoom={16}
            options={{
              styles: mapStyles,
              disableDefaultUI: true,
              zoomControl: true,
            }}
            onLoad={handleMapLoad}
          >
            <Marker
              position={locationData.coordinates}
              title={locationData.name}
              onClick={() => setIsInfoWindowOpen(true)}
            />

            {isInfoWindowOpen && (
              <InfoWindow
                position={locationData.coordinates}
                onCloseClick={() => setIsInfoWindowOpen(false)}
              >
                <div className="p-2">
                  <h4 className="font-bold">{locationData.name}</h4>
                  <p className="text-sm">{locationData.address.street}</p>
                  <p className="text-sm">
                    {locationData.address.city}, {locationData.address.state}{" "}
                    {locationData.address.zip}
                  </p>
                  <p className="text-sm mt-1">
                    Open Daily: {shortFormattedHours}
                  </p>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        ) : (
          <div className="flex items-center justify-center w-full h-full bg-gray-100">
            <div className="w-10 h-10 border-4 border-cannabis-green-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        )}
      </div>

      {/* Directions Button */}
      <div className="mt-6">
        <a
          href={locationData.googleMapsUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block w-full text-center px-6 py-3 bg-cannabis-green-500 hover:bg-cannabis-green-600 text-cannabis-green-950 font-medium rounded-lg transition duration-300"
        >
          Get Directions
        </a>
      </div>
    </div>
  );
};

// Main Component
const Main: React.FC<MainProps> = ({ locationData }) => {
  const sectionRef = useRef<HTMLElement>(null);
  const detailsRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const scrollTriggerInstances = useRef<ScrollTrigger[]>([]);

  useEffect(() => {
    // Apply animations with a small timeout to ensure DOM is ready
    const animationTimeout = setTimeout(() => {
      const triggers = [];

      // Animate location details
      const detailsTrigger = animateLocationDetails(
        { current: detailsRef.current },
        { current: sectionRef.current }
      );
      if (detailsTrigger) triggers.push(detailsTrigger);

      // Animate map
      const mapTrigger = animateMap(
        { current: mapRef.current },
        { current: sectionRef.current }
      );
      if (mapTrigger) triggers.push(mapTrigger);

      // Store triggers for cleanup
      scrollTriggerInstances.current = triggers;
    }, 100);

    // Cleanup function
    return () => {
      clearTimeout(animationTimeout);
      cleanupScrollTriggers();
    };
  }, []);

  return (
    <section ref={sectionRef} className="py-16">
      <div className="container mx-auto px-4">
        <div className="flex flex-col lg:flex-row gap-12">
          {/* Location Details Side */}
          <div ref={detailsRef} className="lg:w-1/2">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Washington DC Dispensary
            </h2>

            <LocationDetails locationData={locationData} />
            <AboutShop city={locationData.address.city} />
          </div>

          {/* Map Side */}
          <div ref={mapRef} className="lg:w-1/2">
            <MapSection locationData={locationData} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Main;
